import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { VideoCall } from "./VideoCall";
import { LiveKitVideoCall } from "./LiveKitVideoCall";
import { CreateRoomModal } from "./CreateRoomModal";
import SummariesHistory from "./SummariesHistory";
import RecordingsList from "./RecordingsList";
import Services from "../services/Services";
import { ScheduleMeetingModal, UpcomingMeetings } from "./MeetingScheduler";
import type { UpcomingMeeting } from "./MeetingScheduler";
import "./MeMeetApp.css";

type Theme = "light" | "dark";

const THEME_KEY = "memeet_theme";

function getInitialTheme(): Theme {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch {}
  return "dark";
}

interface CreateRoomOptions {
  roomID: string;
  isTemporary: boolean;
  isPrivate: boolean;
  passCode?: string;
  title?: string;
  startDateTime?: string;
  endDateTime?: string;
  timezone?: string;
}

/**
 * "Katilmak istiyor" bildirim sesi.
 *
 * Dosya EKLEMEDEN WebAudio ile sentezleniyor. Sebep: memeet-core-ui paketine
 * eklenen YENI bir dosya (ses varligi ya da yardimci modul) pnpm store
 * kopyasina tasinmiyor ve masaustunde runtime import hatasi veriyor
 * (bkz. feedback_memeet_three_platforms, tuzak #2). Tek dosyada kalmak bu
 * tuzagi tamamen atlatiyor.
 *
 * Ton: yukselen iki yumusak sinus (B5 -> E6), kisa ustel sonum, alcak gecirgen
 * filtre. Toplanti sesini bastirmayacak kadar kisik.
 */
let knockChimeCtx: AudioContext | null = null;

const playKnockChime = () => {
  try {
    if (typeof window === "undefined") return;
    const Ctx: typeof AudioContext | undefined =
      (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    if (!knockChimeCtx) knockChimeCtx = new Ctx();
    const ctx = knockChimeCtx;
    if (!ctx) return;
    // Tarayici otomatik oynatmayi askiya almis olabilir; host zaten odaya
    // girerken etkilesime girdigi icin resume() basarili olur.
    if (ctx.state === "suspended") void ctx.resume();

    const master = ctx.createGain();
    master.gain.value = 0.14;
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = "lowpass";
    lowpass.frequency.value = 4200;
    master.connect(lowpass);
    lowpass.connect(ctx.destination);

    const note = (freq: number, delay: number, dur: number) => {
      const t = ctx.currentTime + delay;
      const env = ctx.createGain();
      env.gain.setValueAtTime(0.0001, t);
      env.gain.exponentialRampToValueAtTime(1, t + 0.012);
      env.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      env.connect(master);

      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = freq;
      osc.connect(env);
      osc.start(t);
      osc.stop(t + dur + 0.02);

      // Hafif ust harmonik: parlaklik verir, metalik yapmaz.
      const shimmer = ctx.createOscillator();
      const shimmerGain = ctx.createGain();
      shimmerGain.gain.value = 0.16;
      shimmer.type = "sine";
      shimmer.frequency.value = freq * 2;
      shimmer.connect(shimmerGain);
      shimmerGain.connect(env);
      shimmer.start(t);
      shimmer.stop(t + dur * 0.6);
    };

    note(987.77, 0, 0.42);
    note(1318.51, 0.13, 0.5);
  } catch {
    // Ses cihazi yok ya da politika engelledi: sessizce gec, akisi bozma.
  }
};

export default function MeMeetApp({
  token,
  lodosUserId,
  nickname,
  displayName,
  avatarUrl,
  language,
  changeLanguage,
  availableLanguages,
  getTranslation,
  requestedRoomId,
  onRequestedRoomIdHandled,
  getShareUrl,
  getLinkPreview,
  onSendDmOnSocialHive,
  workspaces,
  onAddChatMessageToChecklist,
  onAddChatMessageToNotes,
  onLogout,
  onRoomChange,
  isPro,
  tokenBalance,
  refreshTokenBalance,
  onGetCollaboratorsService,
  onInviteUserService,
  onIsInvitedUserService,
  onRemoveInvitationService,
  onGetInvitationsService,
  useLiveKit,
  liveKitTokenEndpoint,
}: {
  token?: string;
  lodosUserId?: string;
  nickname?: string;
  displayName?: string;
  avatarUrl?: string;
  language?: string;
  changeLanguage?: (lang: string) => void;
  availableLanguages?: Array<{ code: string; name: string }>;
  getTranslation?: (key: string, params?: Record<string, string>) => string;
  requestedRoomId?: string;
  onRequestedRoomIdHandled?: (roomId: string) => void;
  getShareUrl?: (roomId: string) => string;
  getLinkPreview?: (token: string, url: string) => Promise<any>;
  onSendDmOnSocialHive?: (targetUserId: string, displayName: string) => void | Promise<void>;
  workspaces?: any[];
  onAddChatMessageToChecklist?: (params: any) => Promise<any>;
  onAddChatMessageToNotes?: (params: any) => Promise<any>;
  onLogout?: () => void;
  onRoomChange?: (roomId: string) => void;
  isPro?: boolean;
  tokenBalance?: { balance?: number } | null;
  refreshTokenBalance?: () => void;
  onGetCollaboratorsService?: () => Promise<any>;
  onInviteUserService?: (options: { roomID: string; userID?: string; email?: string; nameLastname?: string }) => Promise<any>;
  onIsInvitedUserService?: (options: { roomID: string; userID?: string; email?: string }) => Promise<any>;
  onRemoveInvitationService?: (options: { invitationCode: string }) => Promise<any>;
  onGetInvitationsService?: (options: { roomID: string }) => Promise<any>;
  /** SFU geçişi için bayrak — true ise LiveKit tabanlı yeni call komponenti render edilir */
  useLiveKit?: boolean;
  /** LiveKit token endpoint override — yoksa default prod.apiollon.com */
  liveKitTokenEndpoint?: string;
}) {
  const [roomId, setRoomId] = useState("");
  const [creatorUserId, setCreatorUserId] = useState<string | undefined>(undefined);
  const [inputRoomId, setInputRoomId] = useState("");
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isCreateRoomModalOpen, setIsCreateRoomModalOpen] = useState(false);
  const [createRoomDefaultId, setCreateRoomDefaultId] = useState(() => `room-${Date.now().toString(36)}`);
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  const [isJoiningRoom, setIsJoiningRoom] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  // Lobi görünümünde sol menüden seçilen sekme: toplantı / geçmiş toplantılar / özet-kayıt
  const [hubView, setHubView] = useState<"meet" | "recent" | "summaries">("meet");
  // Lobide "son toplantılar" (memeet-recent-rooms) — core-ui'daki bölümün standalone portu
  const [recentRooms, setRecentRooms] = useState<Array<{ room: string; last: number }>>([]);

  /* ---------- Toplanti planlama (2026-09-14) ----------
     Standalone surum servisleri DOGRUDAN cagiriyor (paket surumu prop
     aliyor); ikisi de ayni uclara gidiyor. */
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [upcoming, setUpcoming] = useState<UpcomingMeeting[]>([]);
  const [upcomingLoading, setUpcomingLoading] = useState(false);

  const loadUpcoming = useCallback(async () => {
    if (!token) return;
    setUpcomingLoading(true);
    try {
      const r = await Services.MeMeetUpcomingMeetings(token, { days: 14 });
      const items = r?.data?.result?.meetings || [];
      setUpcoming(Array.isArray(items) ? items : []);
    } catch {
      setUpcoming([]);
    } finally {
      setUpcomingLoading(false);
    }
  }, [token]);

  useEffect(() => { void loadUpcoming(); }, [loadUpcoming]);

  const handleSchedule = useCallback(async (payload: {
    title: string; startDateTime: number; endDateTime: number;
    timezone: string; isPrivate: boolean; attendees: Array<{ email: string }>;
  }) => {
    if (!token) return { ok: false };
    try {
      const res = await Services.MeMeetScheduleMeeting(token, payload);
      const data = res?.data?.result;
      if (data?.success) {
        void loadUpcoming();
        return { ok: true, joinLink: data.joinLink as string };
      }
      return { ok: false, message: res?.data?.message };
    } catch (e: any) {
      return { ok: false, message: e?.message };
    }
  }, [token, loadUpcoming]);

  const handleRespond = useCallback(async (roomID: string, response: "yes" | "no" | "maybe") => {
    if (!token) return;
    try { await Services.MeMeetRespondToMeeting(token, { roomID, response }); } catch { /* yut */ }
    void loadUpcoming();
  }, [token, loadUpcoming]);

  const handleCancelMeeting = useCallback(async (roomID: string) => {
    if (!token) return;
    try { await Services.MeMeetCancelMeeting(token, roomID); } catch { /* yut */ }
    void loadUpcoming();
  }, [token, loadUpcoming]);
  const [createRoomError, setCreateRoomError] = useState<string | null>(null);
  const [pendingPrivateRoom, setPendingPrivateRoom] = useState<{ roomID: string; creatorUserId?: string } | null>(null);
  const [passCodeInput, setPassCodeInput] = useState("");
  const [passCodeError, setPassCodeError] = useState<string | null>(null);
  const [isCheckingPassCode, setIsCheckingPassCode] = useState(false);
  // Host-approval "waiting room" join gate
  const [awaitingApproval, setAwaitingApproval] = useState(false);
  const [approvalRejected, setApprovalRejected] = useState(false);
  const [pendingJoin, setPendingJoin] = useState<{ roomID: string; creatorUserId?: string } | null>(null);
  // Host knock queue (pending join requests visible to the room owner)
  const [joinRequests, setJoinRequests] = useState<Array<{ user_id: string; display_name: string; requested_at?: string | number }>>([]);
  const [processingRequests, setProcessingRequests] = useState<Record<string, boolean>>({});
  // Ilk poll'daki bekleyenler icin ses CALMAZ (host odaya girdiginde
  // birikmis 3 istek varsa ust uste calmasin); yalnizca sonradan gelen
  // YENI istekte calar.
  const seenJoinRequestIDsRef = useRef<Set<string> | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const effectiveNickname = useMemo(() => nickname?.trim() || displayName?.trim() || "User", [nickname, displayName]);
  const effectiveDisplayName = useMemo(() => displayName?.trim() || nickname?.trim() || "User", [displayName, nickname]);

  const initials = useMemo(() => {
    const name = effectiveDisplayName || "U";
    const parts = name.split(" ").filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }, [effectiveDisplayName]);

  const t = useCallback(
    (key: string, fallback: string, params?: Record<string, string>) => {
      if (!getTranslation) return fallback;
      const translated = getTranslation(key, params || {});
      if (!translated || translated === key) return fallback;
      return translated;
    },
    [getTranslation]
  );

  const handleThemeChange = useCallback((newTheme: Theme) => {
    setTheme(newTheme);
    try {
      localStorage.setItem(THEME_KEY, newTheme);
    } catch {}
    setIsUserMenuOpen(false);
  }, []);

  const handleLanguageChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      changeLanguage?.(event.target.value);
    },
    [changeLanguage]
  );

  const handleLogout = useCallback(() => {
    setIsUserMenuOpen(false);
    onLogout?.();
  }, [onLogout]);

  const extractRoomId = (input: string): string => {
    const trimmed = input.trim();
    if (!trimmed) return "";

    // Try to parse as URL first
    try {
      const url = new URL(trimmed);
      // Extract roomId from pathname like /memeet/abc123
      const match = url.pathname.match(/\/memeet\/(.+)/);
      if (match) return match[1];
    } catch {
      // Not a valid URL, continue with other checks
    }

    // Check if it's a path like /memeet/abc123
    const pathMatch = trimmed.match(/^\/?memeet\/(.+)/);
    if (pathMatch) return pathMatch[1];

    // Otherwise treat the whole input as roomId
    return trimmed;
  };

  // Actually connect into the room (mounts the call component which fetches the LiveKit token).
  const enterRoom = useCallback(
    (resolvedRoomId: string, resolvedCreatorUserId?: string) => {
      setCreatorUserId(resolvedCreatorUserId);
      setRoomId(resolvedRoomId);
      setInputRoomId(resolvedRoomId);

      // Fire-and-forget: deduct token from room creator for this user joining
      if (token && resolvedCreatorUserId && lodosUserId) {
        Services.MeMeetUserJoin(token, {
          roomCreatorID: resolvedCreatorUserId,
          roomID: resolvedRoomId,
          joiningUserID: lodosUserId,
        }).catch(() => {});
      }
    },
    [token, lodosUserId]
  );

  // Host-approval gate. Runs BEFORE any LiveKit token is fetched.
  // Fail-open: any error → allowed, so a backend hiccup never blocks joining.
  const requestJoinGate = useCallback(
    async (resolvedRoomId: string): Promise<{ allowed: boolean; status: string }> => {
      if (!token) return { allowed: true, status: "ok" };
      try {
        const r = await Services.MeMeetRequestJoin(token, { roomID: resolvedRoomId, name: effectiveDisplayName });
        // Backend iki biçimde dönebiliyor: düz {allowed,status} ya da
        // {status:true,result:{allowed,status}} sarmalı. Önce düz alan, yoksa
        // result içine bak — aksi halde bekleme odası poll'u "allowed"ı hiç görmüyordu.
        const raw = r?.data || {};
        const d = typeof raw.allowed !== "undefined" ? raw : raw.result || raw;
        return {
          allowed: d?.allowed === true,
          status: typeof d?.status === "string" ? d.status : "ok",
        };
      } catch {
        return { allowed: true, status: "error_open" };
      }
    },
    [token, effectiveDisplayName]
  );

  const joinRoom = useCallback(
    async (rawInput: string) => {
      if (!token) return;
      const roomCode = extractRoomId(rawInput);
      if (!roomCode || isJoiningRoom) return;

      setIsJoiningRoom(true);
      setJoinError(null);
      setInputRoomId(roomCode);

      try {
        const response = await Services.GetRoom(token, { roomID: roomCode });
        const data = response?.data;
        const status = data === false ? false : typeof data?.status === "boolean" ? data.status : true;

        if (status === false) {
          setJoinError(t("memeetRoomNotFound", "Room not found."));
          return;
        }

        const resolvedRoomId = data?.result?.roomID || data?.roomID || roomCode;
        const isPrivate = data?.result?.isPrivate === true;
        const resolvedCreatorUserId: string | undefined = data?.result?.creatorUserId || data?.creatorUserId || undefined;

        if (isPrivate) {
          setPendingPrivateRoom({ roomID: resolvedRoomId, creatorUserId: resolvedCreatorUserId });
          return;
        }

        // Host-approval gate — sits immediately before the token+connect flow.
        const gate = await requestJoinGate(resolvedRoomId);
        if (!gate.allowed && gate.status === "needs_approval") {
          setApprovalRejected(false);
          setPendingJoin({ roomID: resolvedRoomId, creatorUserId: resolvedCreatorUserId });
          setAwaitingApproval(true);
          return;
        }
        if (!gate.allowed && gate.status === "denied") {
          setPendingJoin({ roomID: resolvedRoomId, creatorUserId: resolvedCreatorUserId });
          setApprovalRejected(true);
          return;
        }

        enterRoom(resolvedRoomId, resolvedCreatorUserId);
      } catch (error) {
        console.error("Error getting room:", error);
        setJoinError(t("somethingWentWrong", "Something went wrong."));
      } finally {
        setIsJoiningRoom(false);
      }
    },
    [token, isJoiningRoom, t, requestJoinGate, enterRoom]
  );

  const handleJoinRoom = useCallback(() => {
    void joinRoom(inputRoomId);
  }, [joinRoom, inputRoomId]);

  // Lobide "son toplantılar"ı yükle. Ağ hatası lobiyi asla patlatmaz —
  // sessizce boş liste ile devam eder (core-ui ile aynı davranış).
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void (async () => {
      try {
        const r = await Services.MeMeetRecentRooms(token);
        const items = r?.data?.result?.items || r?.data?.items || [];
        if (!cancelled && Array.isArray(items)) setRecentRooms(items);
      } catch {
        // sessizce yut
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Epoch (saniye veya milisaniye) → kısa, göreli zaman etiketi.
  const formatRecentTime = useCallback(
    (last: number) => {
      const ms = last > 1e12 ? last : last * 1000;
      const mins = Math.floor((Date.now() - ms) / 60000);
      if (mins < 1) return t("memeetRecentJustNow", "az önce");
      if (mins < 60) return `${mins} ${t("memeetRecentMinutesShort", "dk önce")}`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs} ${t("memeetRecentHoursShort", "sa önce")}`;
      const days = Math.floor(hrs / 24);
      return `${days} ${t("memeetRecentDaysShort", "gün önce")}`;
    },
    [t]
  );

  const handlePassCodeSubmit = useCallback(async () => {
    if (!token || !pendingPrivateRoom || isCheckingPassCode) return;
    setIsCheckingPassCode(true);
    setPassCodeError(null);
    try {
      const response = await Services.CheckPassCode(token, {
        roomID: pendingPrivateRoom.roomID,
        passCode: passCodeInput,
      });
      const data = response?.data;
      if (data?.status === true) {
        const roomCreatorId = pendingPrivateRoom.creatorUserId;
        const privateRoomId = pendingPrivateRoom.roomID;

        setPendingPrivateRoom(null);
        setPassCodeInput("");
        setPassCodeError(null);

        // Host-approval gate — runs before the token+connect flow.
        const gate = await requestJoinGate(privateRoomId);
        if (!gate.allowed && gate.status === "needs_approval") {
          setApprovalRejected(false);
          setPendingJoin({ roomID: privateRoomId, creatorUserId: roomCreatorId });
          setAwaitingApproval(true);
        } else if (!gate.allowed && gate.status === "denied") {
          setPendingJoin({ roomID: privateRoomId, creatorUserId: roomCreatorId });
          setApprovalRejected(true);
        } else {
          enterRoom(privateRoomId, roomCreatorId);
        }
      } else {
        setPassCodeError(t("memeetPassCodeIncorrect", "Incorrect passcode."));
      }
    } catch {
      setPassCodeError(t("somethingWentWrong", "Something went wrong."));
    } finally {
      setIsCheckingPassCode(false);
    }
  }, [token, pendingPrivateRoom, passCodeInput, isCheckingPassCode, t, requestJoinGate, enterRoom]);

  const handlePassCodeCancel = useCallback(() => {
    setPendingPrivateRoom(null);
    setPassCodeInput("");
    setPassCodeError(null);
  }, []);

  const generateDefaultRoomId = useCallback(() => {
    return `room-${Date.now().toString(36)}`;
  }, []);

  const getDomain = useCallback(() => {
    return typeof window !== "undefined" ? window.location.host : "";
  }, []);

  const handleCreateRoomModalClose = useCallback(() => {
    setIsCreateRoomModalOpen(false);
    setCreateRoomError(null);
  }, []);

  const handleCreateRoom = useCallback(
    async (options: CreateRoomOptions) => {
      if (!token) return;
      setIsCreatingRoom(true);
      setCreateRoomError(null);

      const createRoomPayload: {
        domain: string;
        roomID: string;
        isPrivate: boolean;
        passCode: string | undefined;
        isTemporary: boolean;
        title: string | undefined;
        startDateTime: string | undefined;
        endDateTime: string | undefined;
        timezone: string | undefined;
      } = {
        domain: getDomain(),
        roomID: options.roomID,
        isPrivate: options.isPrivate,
        passCode: options.isPrivate ? options.passCode : undefined,
        isTemporary: options.isTemporary,
        title: undefined,
        startDateTime: undefined,
        endDateTime: undefined,
        timezone: undefined,
      };

      if (options.title?.trim()) createRoomPayload.title = options.title.trim();
      if (options.startDateTime?.trim()) createRoomPayload.startDateTime = options.startDateTime.trim();
      if (options.endDateTime?.trim()) createRoomPayload.endDateTime = options.endDateTime.trim();
      if (options.timezone?.trim()) createRoomPayload.timezone = options.timezone.trim();

      try {
        const response = await Services.CreateRoom(token, createRoomPayload);
        const data = response?.data;
        const status = data === false ? false : typeof data?.status === "boolean" ? data.status : true;

        if (status === true) {
          const createdRoomId = data?.result?.roomID || options.roomID;
          setCreatorUserId(lodosUserId);
          setRoomId(createdRoomId);
          setInputRoomId(createdRoomId);
          setIsCreateRoomModalOpen(false);
          refreshTokenBalance?.();
        } else {
          const msg = data?.message || data?.result?.message || "";
          if (typeof msg === "string" && msg.toLowerCase().includes("insufficient tokens")) {
            setCreateRoomError(
              t("memeetInsufficientTokens", "Insufficient tokens. Please purchase more tokens to create a meeting.")
            );
          } else {
            setCreateRoomError(
              t("memeetRoomIdInUse", "This room ID is already in use. Please try a different room ID.")
            );
          }
        }
      } catch (error) {
        console.error("Error creating room:", error);
        const errorData = (error as { response?: { data?: any } })?.response?.data;
        const status =
          errorData === false
            ? false
            : typeof errorData?.status === "boolean"
              ? errorData.status
              : null;

        if (status === false) {
          const msg = errorData?.message || errorData?.result?.message || "";
          if (typeof msg === "string" && msg.toLowerCase().includes("insufficient tokens")) {
            setCreateRoomError(
              t("memeetInsufficientTokens", "Insufficient tokens. Please purchase more tokens to create a meeting.")
            );
          } else {
            setCreateRoomError(
              t("memeetRoomIdInUse", "This room ID is already in use. Please try a different room ID.")
            );
          }
        } else {
          setCreateRoomError(t("somethingWentWrong", "Something went wrong."));
        }
      } finally {
        setIsCreatingRoom(false);
      }
    },
    [token, getDomain, t]
  );

  const handleNewMeetingClick = useCallback(() => {
    setJoinError(null);
    setCreateRoomError(null);
    if (isPro) {
      setCreateRoomDefaultId(generateDefaultRoomId());
      setIsCreateRoomModalOpen(true);
    } else {
      const newRoomId = generateDefaultRoomId();
      void handleCreateRoom({
        roomID: newRoomId,
        isTemporary: true,
        isPrivate: false,
      });
    }
  }, [isPro, generateDefaultRoomId, handleCreateRoom]);

  const handleCancelApproval = useCallback(() => {
    setAwaitingApproval(false);
    setApprovalRejected(false);
    setPendingJoin(null);
    setIsJoiningRoom(false);
  }, []);

  const handleLeaveRoom = () => {
    setRoomId("");
    setCreatorUserId(undefined);
    setInputRoomId("");
    setJoinError(null);
    setPendingPrivateRoom(null);
    setPassCodeInput("");
    setPassCodeError(null);
    setAwaitingApproval(false);
    setApprovalRejected(false);
    setPendingJoin(null);
    setJoinRequests([]);
    setProcessingRequests({});
  };

  // While waiting for host approval, poll the gate every 3s.
  // allowed → enter the room; denied → show rejected panel; network error → keep waiting.
  useEffect(() => {
    if (!awaitingApproval || !pendingJoin || !token) return;
    let cancelled = false;
    const target = pendingJoin;

    const poll = async () => {
      const gate = await requestJoinGate(target.roomID);
      if (cancelled) return;
      if (gate.allowed) {
        setAwaitingApproval(false);
        setPendingJoin(null);
        enterRoom(target.roomID, target.creatorUserId);
      } else if (gate.status === "denied") {
        setAwaitingApproval(false);
        setApprovalRejected(true);
      }
      // needs_approval / error_open → keep waiting
    };

    const interval = window.setInterval(poll, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [awaitingApproval, pendingJoin, token, requestJoinGate, enterRoom]);

  // Host knock queue — only the room owner polls the pending join requests.
  const isRoomCreator = useMemo(
    () => Boolean(creatorUserId) && String(lodosUserId || "") === String(creatorUserId || ""),
    [creatorUserId, lodosUserId]
  );

  useEffect(() => {
    if (!roomId || !isRoomCreator || !token) return;
    let cancelled = false;
    seenJoinRequestIDsRef.current = null;

    const poll = async () => {
      try {
        const r = await Services.MeMeetListJoinRequests(token, { roomID: roomId });
        if (cancelled) return;
        const items = r?.data?.items || r?.data?.result?.items || [];
        if (Array.isArray(items)) {
          const normalized = items
            .filter((it: any) => it && it.user_id != null)
            .map((it: any) => ({
              user_id: String(it.user_id),
              display_name: String(it.display_name || it.name || ""),
              requested_at: it.requested_at,
            }));
          // De-dupe by user_id
          const seen = new Set<string>();
          const deduped = normalized.filter((it: { user_id: string }) => {
            if (seen.has(it.user_id)) return false;
            seen.add(it.user_id);
            return true;
          });
          const ids = deduped.map((it: { user_id: string }) => it.user_id);
          const previouslySeen = seenJoinRequestIDsRef.current;
          if (previouslySeen === null) {
            // Ilk tur: yalnizca temel al, ses calma.
            seenJoinRequestIDsRef.current = new Set(ids);
          } else {
            const hasNew = ids.some((id: string) => !previouslySeen.has(id));
            seenJoinRequestIDsRef.current = new Set(ids);
            if (hasNew) playKnockChime();
          }
          setJoinRequests(deduped);
        }
      } catch {
        // Ignore polling errors; keep the last known list.
      }
    };

    void poll();
    const interval = window.setInterval(poll, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [roomId, isRoomCreator, token]);

  const handleApproveJoin = useCallback(
    async (userID: string, approve: boolean) => {
      if (!token || !roomId || processingRequests[userID]) return;
      setProcessingRequests((prev) => ({ ...prev, [userID]: true }));
      try {
        await Services.MeMeetApproveJoin(token, { roomID: roomId, userID, approve });
        // Drop it locally right away; the next poll reconciles with the server.
        setJoinRequests((prev) => prev.filter((it) => it.user_id !== userID));
      } catch {
        // Leave the item in place so the host can retry.
      } finally {
        setProcessingRequests((prev) => {
          const next = { ...prev };
          delete next[userID];
          return next;
        });
      }
    },
    [token, roomId, processingRequests]
  );

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isUserMenuOpen]);

  // Linkle gelen oda (requestedRoomId): çekirdek (memeet-core-ui) gibi otomatik
  // joinRoom çağrılır — eskiden yalnız kod kutusu dolduruluyor, kullanıcı linke
  // tıkladığı hâlde bir de "Katıl"a basmak zorunda kalıyordu. joinRoom
  // isJoiningRoom'a bağlı olduğu için her değişiminde effect yeniden koşar;
  // aynı oda için tek tetikleme ref ile garanti edilir.
  const autoJoinedRoomRef = useRef<string>("");
  useEffect(() => {
    const next = String(requestedRoomId || "").trim();
    if (!next) return;
    // Token henüz yoksa joinRoom sessizce döner; oda kimliğini tüketme, token gelince dene.
    if (!token) return;
    if (autoJoinedRoomRef.current === next) return;
    autoJoinedRoomRef.current = next;
    setJoinError(null);
    void (async () => {
      await joinRoom(next);
      onRequestedRoomIdHandled?.(next);
    })();
  }, [requestedRoomId, onRequestedRoomIdHandled, joinRoom, token]);

  // Notify parent when roomId changes (for URL sync)
  useEffect(() => {
    onRoomChange?.(roomId);
  }, [roomId, onRoomChange]);

  // Stable API objects for the Summaries & Records section. Without useMemo these
  // were recreated on every render (e.g. opening the user menu), which changed the
  // child components' `load` callbacks and re-triggered their fetch → the unified
  // spinner flashed on every unrelated re-render.
  const hubSummaryApi = useMemo(
    () => ({
      listSummaries: (opts: any) => Services.MeMeetListSummaries(token || "", opts || {}),
      getSummaryById: (id: any) => Services.MeMeetGetSummaryById(token || "", { id }),
      getRecordingState: (rid: any) => Services.MeMeetGetRecordingState(token || "", { roomID: rid }),
      deleteSummary: (id: any) => Services.MeMeetDeleteSummary(token || "", { id }),
    }),
    [token]
  );
  const hubRecordingApi = useMemo(
    () => ({
      listRecordings: () => Services.MeMeetListVideoRecordings(token || ""),
      getRecordingLink: (id: any) => Services.MeMeetGetVideoRecordingLink(token || "", { id }),
    }),
    [token]
  );

  if (!token) {
    return (
      <div className={`memeet-root theme-${theme}`} data-theme={theme}>
        <div className="meet-shell">
          <header className="meet-topbar">
            <div className="meet-brand">MeMeet</div>
          </header>
          <main className="meet-main">
            <div className="meet-card">
              <h1>{t("memeetLoginRequiredTitle", "Login required")}</h1>
              <p>{t("memeetLoginRequiredDescription", "You can't use MeMeet without signing in with your Lodos account.")}</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={`memeet-root theme-${theme}`} data-theme={theme}>
      <div className="meet-shell">
        {(awaitingApproval || approvalRejected) && !roomId && (
          <div className="memeet-approval-overlay" role="dialog" aria-modal="true">
            <div className="memeet-approval-card">
              {approvalRejected ? (
                <>
                  <div className="memeet-approval-icon rejected">
                    <RejectedIcon />
                  </div>
                  <h2>{t("memeetApprovalRejected", "Katılımınız reddedildi")}</h2>
                  <div className="memeet-approval-actions">
                    <button className="meet-button" type="button" onClick={handleCancelApproval}>
                      {t("memeetCancel", "Vazgeç")}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="memeet-approval-spinner" aria-hidden="true" />
                  <h2>{t("memeetAwaitingApproval", "Toplantı sahibinin onayı bekleniyor...")}</h2>
                  <div className="memeet-approval-actions">
                    <button className="meet-button" type="button" onClick={handleCancelApproval}>
                      {t("memeetCancel", "Vazgeç")}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {roomId && isRoomCreator && joinRequests.length > 0 && (
          <div className="memeet-knock-queue">
            {joinRequests.map((req) => (
              <div className="memeet-knock-card" key={req.user_id}>
                <div className="memeet-knock-text">
                  {(() => {
                    const knockName = req.display_name || t("memeetSomeone", "Bir katılımcı");
                    return t("memeetKnockRequest", knockName + " katılmak istiyor", { name: knockName });
                  })()}
                </div>
                <div className="memeet-knock-actions">
                  <button
                    className="meet-button primary"
                    type="button"
                    disabled={Boolean(processingRequests[req.user_id])}
                    onClick={() => void handleApproveJoin(req.user_id, true)}
                  >
                    {t("memeetAdmit", "Kabul")}
                  </button>
                  <button
                    className="meet-button"
                    type="button"
                    disabled={Boolean(processingRequests[req.user_id])}
                    onClick={() => void handleApproveJoin(req.user_id, false)}
                  >
                    {t("memeetDeny", "Reddet")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {!roomId ? (
          <>
            <header className="meet-topbar">
              {tokenBalance?.balance != null && (
                <div className="meet-token-badge" title={`${tokenBalance.balance.toLocaleString()} tokens`}>
                  <span className="token-icon">⚡</span>
                  <span className="token-value">
                    {tokenBalance.balance >= 1_000_000
                      ? (tokenBalance.balance / 1_000_000).toFixed(1) + "M"
                      : tokenBalance.balance >= 1_000
                        ? (tokenBalance.balance / 1_000).toFixed(0) + "K"
                        : tokenBalance.balance}
                  </span>
                </div>
              )}
              <div className="meet-user" ref={userMenuRef}>
                <button
                  className="user-menu-trigger"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  type="button"
                >
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="" className="user-avatar" />
                  ) : (
                    <span className="user-avatar-initials">{initials}</span>
                  )}
                  <span className="user-name">{effectiveDisplayName}</span>
                  <ChevronIcon />
                </button>

                {isUserMenuOpen && (
                  <div className="user-menu-dropdown">
                    <div className="user-menu-header">
                      {avatarUrl ? (
                        <img src={avatarUrl} alt="" className="menu-avatar" />
                      ) : (
                        <span className="menu-avatar-initials">{initials}</span>
                      )}
                      <div className="menu-user-info">
                        <span className="menu-user-name">{effectiveDisplayName}</span>
                      </div>
                    </div>

                    <div className="user-menu-divider" />

                    <div className="user-menu-section">
                      <span className="menu-section-label">{t("memeetLanguage", "Language")}</span>
                      <select
                        className="menu-language-select"
                        value={language || ""}
                        onChange={handleLanguageChange}
                        disabled={!changeLanguage || !availableLanguages?.length}
                      >
                        {(availableLanguages || []).map((lang) => (
                          <option key={lang.code} value={lang.code}>
                            {lang.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="user-menu-divider" />

                    <div className="user-menu-section">
                      <span className="menu-section-label">{t("memeetTheme", "Theme")}</span>
                      <div className="theme-options">
                        <button
                          className={`theme-option ${theme === "light" ? "active" : ""}`}
                          onClick={() => handleThemeChange("light")}
                          type="button"
                        >
                          <SunIcon />
                          <span>{t("memeetLightTheme", "Light")}</span>
                        </button>
                        <button
                          className={`theme-option ${theme === "dark" ? "active" : ""}`}
                          onClick={() => handleThemeChange("dark")}
                          type="button"
                        >
                          <MoonIcon />
                          <span>{t("memeetDarkTheme", "Dark")}</span>
                        </button>
                      </div>
                    </div>

                    <div className="user-menu-divider" />

                    <button className="user-menu-item logout" onClick={handleLogout} type="button">
                      <LogoutIcon />
                      <span>{t("memeetLogout", "Log out")}</span>
                    </button>
                  </div>
                )}
              </div>
            </header>
            <main className="meet-main">
              <div className="meet-hub">
                <aside className="meet-hub-aside">
                  <div className="meet-hub-brand">
                    <span className="meet-hub-brand-logo">
                      <MeetTabIcon />
                    </span>
                    <span className="meet-hub-brand-name">MeMeet</span>
                  </div>
                  <div className="meet-hub-hero">
                    <h1 className="meet-hub-hero-title">
                      {t("memeetHeroTitle", "Meet, share and stay in sync.")}
                    </h1>
                    <p className="meet-hub-hero-sub">
                      {t(
                        "memeetHeroSubtitle",
                        "A complete video meeting space with permanent rooms, live chat, screen sharing, and seamless collaboration tools."
                      )}
                    </p>
                  </div>

                  <nav className="meet-hub-nav" aria-label={t("memeetStartOrJoinTitle", "Start or join a video meeting")}>
                    <button
                      type="button"
                      className={`meet-hub-tab ${hubView === "meet" ? "active" : ""}`}
                      onClick={() => setHubView("meet")}
                      aria-pressed={hubView === "meet"}
                    >
                      <span className="meet-hub-tab-icon">
                        <MeetTabIcon />
                      </span>
                      <span className="meet-hub-tab-text">
                        <span className="meet-hub-tab-label">
                          {t("memeetHubTabMeet", "Join or Create a Meeting")}
                        </span>
                        <span className="meet-hub-tab-desc">
                          {t("memeetHubTabMeetDesc", "Start a new call or join with a code.")}
                        </span>
                      </span>
                    </button>

                    <button
                      type="button"
                      className={`meet-hub-tab ${hubView === "recent" ? "active" : ""}`}
                      onClick={() => setHubView("recent")}
                      aria-pressed={hubView === "recent"}
                    >
                      <span className="meet-hub-tab-icon">
                        <HistoryIcon />
                      </span>
                      <span className="meet-hub-tab-text">
                        <span className="meet-hub-tab-label">
                          {t("memeetHubTabRecent", "Last Meetings")}
                        </span>
                        <span className="meet-hub-tab-desc">
                          {t("memeetHubTabRecentDesc", "Jump back into your recent rooms.")}
                        </span>
                      </span>
                    </button>

                    <button
                      type="button"
                      className={`meet-hub-tab ${hubView === "summaries" ? "active" : ""}`}
                      onClick={() => setHubView("summaries")}
                      aria-pressed={hubView === "summaries"}
                    >
                      <span className="meet-hub-tab-icon">
                        <SummaryIcon />
                      </span>
                      <span className="meet-hub-tab-text">
                        <span className="meet-hub-tab-label">
                          {t("memeetHubTabSummaries", "Summaries and Records")}
                        </span>
                        <span className="meet-hub-tab-desc">
                          {t("memeetHubTabSummariesDesc", "Browse meeting summaries and recordings.")}
                        </span>
                      </span>
                    </button>
                  </nav>
                </aside>

                <section className="meet-hub-panel">
                  <div className="meet-hub-panel-inner">
                    {hubView === "meet" ? (
                      <div className="meet-hub-view meet-hub-view-meet">
                        <div className="landing-illustration">
                          <MeetIcon />
                        </div>
                        <h1>{t("memeetStartOrJoinTitle", "Start or join a video meeting")}</h1>
                        <p>{t("memeetStartOrJoinDescription", "Join with a code or create a new meeting.")}</p>

                        <div className="landing-actions">
                          <button
                            className="meet-button primary"
                            onClick={handleNewMeetingClick}
                            disabled={isCreatingRoom}
                          >
                            {isCreatingRoom
                              ? t("memeetCreating", "Creating...")
                              : t("memeetNewMeeting", "New meeting")}
                          </button>

                          <button className="meet-button" onClick={() => setScheduleOpen(true)}>
                            {t("memeetSchedule", "Schedule")}
                          </button>

                          <div className="landing-join">
                            <div className="landing-join-row">
                              <input
                                type="text"
                                placeholder={t("memeetEnterCodePlaceholder", "Enter a code")}
                                value={inputRoomId}
                                aria-invalid={Boolean(joinError)}
                                onChange={(e) => {
                                  setInputRoomId(e.target.value);
                                  if (joinError) setJoinError(null);
                                }}
                                onKeyDown={(e) => e.key === "Enter" && handleJoinRoom()}
                              />
                              <button
                                className="meet-button"
                                onClick={handleJoinRoom}
                                disabled={isJoiningRoom || !inputRoomId.trim()}
                              >
                                {t("memeetJoin", "Join")}
                              </button>
                            </div>
                            {joinError ? (
                              <div className="landing-join-error" role="alert">
                                {joinError}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        {/* Yaklasan toplantilar: hem kurdugum hem davetli
                            oldugum. Bos oldugunda hic cizilmez. */}
                        <UpcomingMeetings
                          meetings={upcoming}
                          loading={upcomingLoading}
                          locale={language || "en"}
                          onJoin={(roomID) => { setInputRoomId(roomID); void joinRoom(roomID); }}
                          onRespond={handleRespond}
                          onCancel={handleCancelMeeting}
                          t={t}
                        />
                      </div>
                    ) : null}

                    {hubView === "recent" ? (
                      <div className="meet-hub-view meet-hub-view-recent">
                        <h2 className="meet-hub-view-title">
                          {t("memeetHubTabRecent", "Last Meetings")}
                        </h2>
                        {recentRooms.length > 0 ? (
                          <ul className="landing-recent-list">
                            {recentRooms.map((r) => (
                              <li key={r.room}>
                                <button
                                  type="button"
                                  className="landing-recent-item"
                                  onClick={() => void joinRoom(r.room)}
                                  disabled={isJoiningRoom}
                                >
                                  <span className="landing-recent-name">{r.room}</span>
                                  <span className="landing-recent-time">{formatRecentTime(r.last)}</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="meet-hub-empty">
                            {t("memeetNoRecentRooms", "No recent meetings yet.")}
                          </div>
                        )}
                      </div>
                    ) : null}

                    {hubView === "summaries" ? (
                      <div className="meet-hub-view meet-hub-view-summaries">
                        <SummariesRecordsSection
                          t={t}
                          summaryApi={hubSummaryApi}
                          recordingApi={hubRecordingApi}
                        />
                      </div>
                    ) : null}
                  </div>
                </section>
              </div>
            </main>
            <ScheduleMeetingModal
              open={scheduleOpen}
              onClose={() => setScheduleOpen(false)}
              onSchedule={handleSchedule}
              t={t}
            />
            <CreateRoomModal
              isOpen={isCreateRoomModalOpen}
              onClose={handleCreateRoomModalClose}
              onCreateRoom={handleCreateRoom}
              defaultRoomId={createRoomDefaultId}
              getTranslation={getTranslation}
              isCreating={isCreatingRoom}
              errorMessage={createRoomError}
              onRoomIdChange={() => {
                if (createRoomError) setCreateRoomError(null);
              }}
            />
            {pendingPrivateRoom && (
              <div
                className="create-room-modal-overlay"
                onClick={handlePassCodeCancel}
                onKeyDown={(e) => e.key === "Escape" && handlePassCodeCancel()}
              >
                <div className="create-room-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="passcode-modal-body">
                    <div className="passcode-modal-icon">
                      <LockIcon />
                    </div>
                    <h2>{t("memeetPrivateRoomTitle", "Private Room")}</h2>
                    <p>{t("memeetPrivateRoomPassCodePrompt", "This room requires a passcode to join.")}</p>
                    <div className="create-room-field">
                      <input
                        type="password"
                        placeholder={t("memeetEnterPassCode", "Enter passcode")}
                        value={passCodeInput}
                        autoFocus
                        onChange={(e) => {
                          setPassCodeInput(e.target.value);
                          if (passCodeError) setPassCodeError(null);
                        }}
                        onKeyDown={(e) => e.key === "Enter" && handlePassCodeSubmit()}
                      />
                      {passCodeError && (
                        <span className="passcode-error">{passCodeError}</span>
                      )}
                    </div>
                    <div className="create-room-modal-actions">
                      <button
                        className="meet-button"
                        onClick={handlePassCodeCancel}
                        type="button"
                      >
                        {t("memeetCancel", "Cancel")}
                      </button>
                      <button
                        className="meet-button primary"
                        onClick={handlePassCodeSubmit}
                        disabled={isCheckingPassCode || !passCodeInput.trim()}
                        type="button"
                      >
                        {isCheckingPassCode
                          ? t("memeetVerifying", "Verifying...")
                          : t("memeetEnter", "Enter")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <main className="meet-main call">
            {useLiveKit ? (
              <LiveKitVideoCall
                roomId={roomId}
                token={token || undefined}
                lodosUserId={lodosUserId}
                nickname={effectiveNickname}
                displayName={effectiveDisplayName}
                avatarUrl={avatarUrl}
                language={language}
                changeLanguage={changeLanguage}
                getTranslation={getTranslation}
                getShareUrl={getShareUrl}
                summaryApi={{
                  setRecording: (rid, on) => Services.MeMeetSetRecording(token || "", { roomID: rid, on }),
                  summarize: (rid) => Services.MeMeetMeetingSummarize(token || "", { roomID: rid }),
                  getSummary: (rid) => Services.MeMeetGetMeetingSummary(token || "", { roomID: rid }),
                  listSummaries: (opts: any) => Services.MeMeetListSummaries(token || "", opts || {}),
                  getSummaryById: (id: any) => Services.MeMeetGetSummaryById(token || "", { id }),
                  getRecordingState: (rid: any) => Services.MeMeetGetRecordingState(token || "", { roomID: rid }),
                  deleteSummary: (id: any) => Services.MeMeetDeleteSummary(token || "", { id }),
                }}
                getLinkPreview={getLinkPreview}
                onSendDmOnSocialHive={onSendDmOnSocialHive}
                workspaces={workspaces}
                onAddChatMessageToChecklist={onAddChatMessageToChecklist}
                onAddChatMessageToNotes={onAddChatMessageToNotes}
                onGetCollaboratorsService={onGetCollaboratorsService}
                onInviteUserService={onInviteUserService}
                onIsInvitedUserService={onIsInvitedUserService}
                onRemoveInvitationService={onRemoveInvitationService}
                onGetInvitationsService={onGetInvitationsService}
                creatorUserId={creatorUserId}
                liveKitTokenEndpoint={liveKitTokenEndpoint}
                onGetRoomParticipants={async (rid: string) => {
                  if (!token) return [];
                  try {
                    const r = await Services.MeMeetRoomParticipants(token, { roomID: rid });
                    const items = r?.data?.result?.items || r?.data?.items || [];
                    return Array.isArray(items) ? items : [];
                  } catch {
                    return [];
                  }
                }}
                onMuteParticipant={async (options) => {
                  if (!token) return false;
                  try {
                    const r = await Services.MeMeetMuteParticipant(token, options);
                    const d = r?.data;
                    if (d === false) return false;
                    if (typeof d?.status === "boolean" && d.status === false) return false;
                    if (typeof d?.success === "boolean") return d.success;
                    return true;
                  } catch {
                    return false;
                  }
                }}
                onStartVideoRecording={async (options) => {
                  if (!token) return false;
                  try {
                    const r = await Services.MeMeetStartVideoRecording(token, options);
                    const d = r?.data;
                    if (d === false) return false;
                    if (typeof d?.status === "boolean" && d.status === false) return false;
                    if (typeof d?.success === "boolean") return d.success;
                    return true;
                  } catch {
                    return false;
                  }
                }}
                onStopVideoRecording={async (options) => {
                  if (!token) return false;
                  try {
                    const r = await Services.MeMeetStopVideoRecording(token, options);
                    const d = r?.data;
                    if (d === false) return false;
                    if (typeof d?.status === "boolean" && d.status === false) return false;
                    if (typeof d?.success === "boolean") return d.success;
                    return true;
                  } catch {
                    return false;
                  }
                }}
                onGetVideoRecordingState={async (options) => {
                  if (!token) return false;
                  try {
                    const r = await Services.MeMeetVideoRecordingState(token, options);
                    const d = r?.data && r.data.result !== undefined ? r.data.result : r?.data;
                    if (d && typeof d.recording === "boolean") return d.recording;
                    return false;
                  } catch {
                    return false;
                  }
                }}
                onExitRoom={handleLeaveRoom}
                onCallEnd={handleLeaveRoom}
              />
            ) : (
              <VideoCall
                roomId={roomId}
                token={token}
                lodosUserId={lodosUserId}
                nickname={effectiveNickname}
                displayName={effectiveDisplayName}
                avatarUrl={avatarUrl}
                language={language}
                changeLanguage={changeLanguage}
                getTranslation={getTranslation}
                getShareUrl={getShareUrl}
                summaryApi={{
                  setRecording: (rid, on) => Services.MeMeetSetRecording(token || "", { roomID: rid, on }),
                  summarize: (rid) => Services.MeMeetMeetingSummarize(token || "", { roomID: rid }),
                  getSummary: (rid) => Services.MeMeetGetMeetingSummary(token || "", { roomID: rid }),
                  listSummaries: (opts: any) => Services.MeMeetListSummaries(token || "", opts || {}),
                  getSummaryById: (id: any) => Services.MeMeetGetSummaryById(token || "", { id }),
                  getRecordingState: (rid: any) => Services.MeMeetGetRecordingState(token || "", { roomID: rid }),
                  deleteSummary: (id: any) => Services.MeMeetDeleteSummary(token || "", { id }),
                }}
                getLinkPreview={getLinkPreview}
                onSendDmOnSocialHive={onSendDmOnSocialHive}
                workspaces={workspaces}
                onAddChatMessageToChecklist={onAddChatMessageToChecklist}
                onAddChatMessageToNotes={onAddChatMessageToNotes}
                onGetCollaboratorsService={onGetCollaboratorsService}
                onInviteUserService={onInviteUserService}
                onIsInvitedUserService={onIsInvitedUserService}
                onRemoveInvitationService={onRemoveInvitationService}
                onGetInvitationsService={onGetInvitationsService}
                creatorUserId={creatorUserId}
                onExitRoom={handleLeaveRoom}
                onCallEnd={handleLeaveRoom}
              />
            )}
          </main>
        )}
      </div>
    </div>
  );
}

// "Summaries and Records" görünümü: iki listeyi (özetler + kayıtlar) tek bir
// spinner arkasında bekletir; her ikisi de yüklenene kadar tek bir yükleme
// göstergesi gösterir, sonra ikisini birden ortaya çıkarır.
function SummariesRecordsSection({
  summaryApi,
  recordingApi,
  t,
}: {
  summaryApi: any;
  recordingApi: any;
  t: (k: string, fb?: string, vars?: any) => string;
}) {
  const [sumLoading, setSumLoading] = useState(true);
  const [recLoading, setRecLoading] = useState(true);
  const ready = !sumLoading && !recLoading;

  return (
    <>
      {!ready && (
        <div className="meet-hub-loading" role="status" aria-live="polite">
          <span className="meet-hub-spinner" aria-hidden="true" />
          <span className="meet-hub-loading-text">{t("memeetLoading", "Loading…")}</span>
        </div>
      )}
      <div className={`meet-hub-sr ${ready ? "" : "meet-hub-sr-hidden"}`}>
        <SummariesHistory summaryApi={summaryApi} t={t} onLoadingChange={setSumLoading} />
        <RecordingsList recordingApi={recordingApi} t={t} onLoadingChange={setRecLoading} />
      </div>
    </>
  );
}

function RejectedIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function MeetIcon() {
  return (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 7a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7z" />
      <path d="M17 10l4-2v8l-4-2v-4z" />
    </svg>
  );
}

function MeetTabIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 7a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7z" />
      <path d="M17 10l4-2v8l-4-2v-4z" />
    </svg>
  );
}

function HistoryIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v5h5" />
      <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function SummaryIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-2" />
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M8 11h8M8 15h5" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="5" />
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}
